import { useMutation } from '@tanstack/react-query';

import { preflightImportUpdateWorkflow } from '@/lib/api/generated/api';
import type { ImportUpdatePreflight } from '@/lib/api/generated/model';
import { WorkflowRejectedError } from '../errors';

/**
 * Asks what an in-place update would do — which tasks block it, which would
 * move to Backlog — without applying it (spec 201 US3, FR-002).
 *
 * Modelled on `usePreflightWorkflow`: a mutation, not a query, because the
 * answer depends on what is running against the workflow right now, not on
 * anything worth caching under the file.
 */
export function usePreflightImportUpdate(workspaceId: string) {
  return useMutation({
    mutationFn: async ({
      workflowId,
      file,
    }: {
      workflowId: string;
      file: File;
    }): Promise<ImportUpdatePreflight> => {
      const response = await preflightImportUpdateWorkflow(
        workspaceId,
        workflowId,
        { file },
      );
      if (response.status === 200) return response.data;
      if (response.status === 413) {
        throw new WorkflowRejectedError(response.status, response.data);
      }
      throw new Error('This file could not be checked.');
    },
  });
}
